import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import adminService from '@/services/adminService';
import userStoreService from '@/services/userStoreService';
import { toast } from '@/components/ui/use-toast';
import { Building, ChevronDown } from 'lucide-react';

interface StoreSelectorProps {
  /** 'header' for standalone header display, 'dropdown' for use in dropdown menu */
  variant?: 'header' | 'dropdown';
}

export default function StoreSelector({ variant = 'header' }: StoreSelectorProps) {
  const { user, switchStore } = useAuth();
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [isSwitching, setIsSwitching] = useState(false);

  const isJonathonPope = user?.email === 'jonp4208@gmail.com';
  const userLoaded = !!user;

  // For super admin: fetch ALL stores
  const { data: allStores, isLoading: loadingAllStores } = useQuery({
    queryKey: ['admin-stores-selector'],
    queryFn: adminService.getAllStores,
    enabled: userLoaded && isJonathonPope,
    staleTime: 30 * 1000, // Cache for 30 seconds
    retry: 2,
  });

  // For regular users: fetch only accessible stores
  const { data: accessibleData, isLoading: loadingAccessible } = useQuery({
    queryKey: ['user-accessible-stores'],
    queryFn: userStoreService.getAccessibleStores,
    enabled: userLoaded && !isJonathonPope,
    staleTime: 30 * 1000, // Cache for 30 seconds
    retry: 2,
  });

  // Determine which stores to show
  const stores = isJonathonPope
    ? allStores
    : accessibleData?.stores;
  const isLoading = !userLoaded || (isJonathonPope ? loadingAllStores : loadingAccessible);
  const hasMultiStoreAccess = isJonathonPope || (accessibleData?.hasMultiStoreAccess ?? false);

  // Set the initial selected store
  useEffect(() => {
    if (user?.store?._id) {
      setSelectedStoreId(user.store._id);
    }
  }, [user]);

  // Handle store change
  const handleStoreChange = async (storeId: string) => {
    // Prevent concurrent switches
    if (storeId === selectedStoreId || isSwitching) return;

    setIsSwitching(true);

    try {
      toast({
        title: 'Switching Store...',
        description: 'Please wait while we update your store',
      });

      await switchStore(storeId);

      toast({
        title: 'Store Changed',
        description: 'Successfully switched store. Refreshing page...',
      });

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to switch store. Please try again.',
        variant: 'destructive',
      });
      setSelectedStoreId(user?.store?._id || '');
      setIsSwitching(false);
    }
  };

  // Don't render while loading - wait for data
  if (isLoading) {
    return null;
  }

  // Don't render if no multi-store access or no stores available
  if (!hasMultiStoreAccess || !stores || stores.length <= 1) {
    return null;
  }

  // Sort stores alphabetically by name
  const sortedStores = [...stores].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  // Dropdown variant for the profile menu
  if (variant === 'dropdown') {
    const currentStore = sortedStores.find(s => s._id === selectedStoreId);
    return (
      <div className="px-1 py-2 border-b border-gray-100 mb-1 w-full overflow-hidden">
        <div className="text-xs font-medium text-gray-500 mb-2 px-1">Switch Store</div>
        <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-2 py-2 min-w-0">
          <Building className="h-4 w-4 text-teal-600 flex-shrink-0" />
          <select
            className="flex-1 min-w-0 bg-transparent text-sm font-medium text-gray-900 appearance-none cursor-pointer focus:outline-none disabled:opacity-50 truncate"
            value={selectedStoreId}
            onChange={(e) => handleStoreChange(e.target.value)}
            disabled={isLoading || isSwitching}
          >
            {sortedStores.map((store) => (
              <option key={store._id} value={store._id}>
                {store.name} (#{store.storeNumber})
              </option>
            ))}
          </select>
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 pointer-events-none" />
        </div>
      </div>
    );
  }

  // Header variant (original layout)
  return (
    <div className="relative">
      <div className="flex items-center gap-3 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-sm border border-white/20 hover:bg-white hover:shadow-md transition-all duration-200 min-w-[280px]">
        <Building className="h-5 w-5 text-gray-600 flex-shrink-0" />
        <div className="flex-1 relative">
          <select
            className="w-full bg-transparent text-gray-900 font-medium text-sm appearance-none cursor-pointer focus:outline-none pr-6 disabled:opacity-50"
            value={selectedStoreId}
            onChange={(e) => handleStoreChange(e.target.value)}
            disabled={isLoading || isSwitching}
          >
            <option value="" disabled>
              Select store
            </option>
            {sortedStores.map((store) => (
              <option key={store._id} value={store._id} className="py-2">
                {store.name} (#{store.storeNumber})
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-0 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
        </div>
      </div>
    </div>
  );
}
